import _ from 'lodash-es';
import State from '~/scripts/services/model/state';
import Action from '~/scripts/services/model/action';

/* globals Knitiv */

export default class WfNodeEditorController {
	/**
	 * @type {{
	 *   isa:  import('@knitiv/api-client-javascript').KNode,
	 *   kid:  import('@knitiv/api-client-javascript').KNode,
	 *   name: string
	 * }}
	 */
	#roleTous;

	/* @ngInject */
	/**
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').IWindowService} $window
	 * @param {any} NodeService
	 * @param {any} Notification
	 * @param {any} User
	 */
	constructor(
		$rootScope,
		$scope,
		$timeout,
		$translate,
		$window,
		NodeService,
		Notification,
		User,
	) {
		this.#roleTous = {
			isa: 'K_NODE;KROLE',
			kid: 'K_NODE;KR_WF_TOUS',
			name: $translate.instant('common.all'),
		};
		this.$rootScope = $rootScope;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$window = $window;
		this.NodeService = NodeService;
		this.Notification = Notification;
		this.User = User;

		this.context = this.$rootScope.context;

		/** @type {boolean} */
		this.create = false;

		/** @type {boolean} */
		this.disabled = true;

		/** @type {?import('../wf-editor/wf-editor.controller').default} */
		this.editor = null;

		/** @type {?(import('angular').IFormController)} */
		this.formNode = null;

		/** @type {any} */
		this.jsonEditorOptions = {
			mode:    this.disabled ? 'view' : 'tree',
			modes:   this.disabled ? [] : [
				'code',
				'form',
				'text',
				'tree',
				'view',
			], // allowed modes
			onError: (/** @type {any} */ error) => {
				this.Notification.warning({
					title:   this.$translate.instant('error.title'),
					message: error.toString(),
				});
			},
		};

		/** @type {any} */
		this.mf = {
			card:              '0-N',
			allocators:        [],
			delegationGranted: [],
			custom:            {
				selectable: [
					'K_NODE;WF_ROLE',
					'K_NODE;KPROFIL',
				],
			},
			validate:          true,
		};

		/** @type {?(State | Action)} */
		this.node = null;

		/** @type {any} */
		this.operations = undefined;

		/**
		 * @type {?{
		 *  create: boolean,
		 *  modified: boolean,
		 *  node: import('~/scripts/services/model/action').default | import('~/scripts/services/model/state').default,
		 *  operations: any,
		 *  tab: 'info' | 'droit' | 'allocator' | 'delegation'
		 * }}
		 */
		this.selected = null;

		/** @type {State[]} */
		this.states = [];

		/** @type {any} */
		this.tmp = {
			from: [],
			to:   [],
		};
	}

	cancel() {
		this.selected = null;
		if (this.editor?.tab) {
			if (this.editor.tab.selected) {
				this.editor.tab.selected.node.selected = false;
				this.editor.tab.selected = null;
			}
			if (this.node && this.create) {
				if (this.node instanceof State) {
					this.editor.tab.stateDelete(this.node);
				}
				else if (this.node instanceof Action) {
					this.editor.tab.actionDelete(this.node);
				}
			}
		}
		this.node = null;
	}

	/**
	 * @param {string} name
	 */
	async createRole(name) {
		if (name) {
			const role = this.NodeService.create('K_NODE;WF_ROLE', `K_NODE;${this.User.getRef()}`);
			role.setName(name);

			await this.NodeService.update(role);
			this.Notification.success({
				title:   this.$translate.instant('object.successfully-created-title'),
				message: this.$translate.instant('object.successfully-created-message'),
			});
			/** @deprecated */
			this.$scope.$emit('admin-wf-load-roles');

			this.mf.allocators.at(-1).kid = role.kid;
			this.editor?.tab?.schema.addRole({
				kid:  role.kid,
				name: role.name,
				isa:  role.isa.objid,
			});
		}
	}

	delete() {
		if (this.node) {
			this.editor?.tab?.nodeDelete(this.node);
		}
	}

	/**
	 * @returns {boolean}
	 */
	isAdmin() {
		return !!this.$rootScope?.context?.profil.admin
			|| this.$rootScope.context?.role.sql_in.includes('K_NODE;KR_ADMIN_WF')
			|| false;
	}

	/**
	 * @param {string} op
	 * @param {any} flow
	 * @param {any} item
	 */
	setOp(op, flow, item) {
		if (item && this.selected) {
			if (
				item.kid in this.selected.operations
				&& (
					(this.selected.operations[item.kid].op === 'DEL' && op === 'ADD')
					|| (this.selected.operations[item.kid].op === 'ADD' && op === 'DEL')
				)
			) {
				delete this.selected.operations[item.kid];
			}
			else {
				this.selected.operations[item.kid] = {
					op,
					flow,
					item,
				};
			}
		}
	}

	/**
	 */
	submit() {
		if (this.node) {
			// Garde-fou : submit() reconstruit node.allocators à partir de la liste affichée.
			// Un bug de course encore non identifié vide parfois cette liste sans action de
			// l'utilisateur (voir le $watchCollection de diagnostic dans $onInit) ; enregistrer
			// dans cet état effacerait définitivement les affectataires de l'étape.
			//
			// Retirer tous les affectataires reste légitime (mf.card vaut '0-N', et l'interface
			// propose « Tout supprimer »), donc on ne l'interdit pas : on le fait confirmer.
			// La confirmation est un window.confirm et non la modale de l'application, parce que
			// submit() est appelé de façon synchrone depuis un $broadcast : le rendre asynchrone
			// laisserait nodeEdit() réaffecter `selected` en pleine soumission.
			const willRemoveAll = this.mf.allocators.length === 0 && this.node.allocators.length > 0;
			if (willRemoveAll) {
				console.warn(
					'[wf-node-editor] l\'enregistrement va retirer tous les affectataires de l\'étape.',
					{
						nodeKid:        this.node.kid,
						nodeAllocators: [...this.node.allocators],
					},
				);
			}

			// Texte en clair volontairement : les clés de traduction vivent dans @knitiv/locales,
			// et une clé absente s'afficherait telle quelle.
			if (willRemoveAll && !this.$window.confirm(
				`Cette étape compte ${this.node.allocators.length} affectataire(s), `
				+ 'mais la liste est vide à l\'écran.\n\n'
				+ 'Enregistrer va les retirer définitivement.\n\n'
				+ 'Si vous n\'avez pas vidé la liste vous-même, annulez et rechargez la page.',
			)) {
				this.Notification.warning({
					title:   'Affectataires conservés',
					message: 'Enregistrement des affectataires annulé. Rechargez la page pour '
						+ 'retrouver la liste.',
				});
			}
			else {
				this.node.allocators.length = 0;
				for (const allocator of this.mf.allocators) {
					try {
						this.node.addAllocator(allocator.kid);
						if (!(this.editor?.tab?.schema.kidlist.some((/** @type {any} */ r) => r.kid === allocator.kid))) {
							this.editor?.tab?.schema.addRole({
								kid:  allocator.kid,
								name: allocator.name,
								isa:  allocator.isa,
							});
						}
					}
					catch (error) {
						console.error(error);
					}
				}
			}
		}

		if (this.node instanceof State) {
			if (!Array.isArray(this.node.delegationGranted)) {
				this.node.delegationGranted = [];
			}

			this.node.delegationGranted.length = 0;
			for (const delegationGranted of this.mf.delegationGranted) {
				try {
					this.node.addDelegationGranted(delegationGranted.kid);
					if (!(this.editor?.tab?.schema.kidlist.some((/** @type {any} */ dg) => dg.kid === delegationGranted.kid))) {
						this.editor?.tab?.schema.addDelegationGranted({
							kid:  delegationGranted.kid,
							name: delegationGranted.name,
							isa:  delegationGranted.isa,
						});
					}
				}
				catch (error) {
					console.error(error);
				}
			}
		}

		if (this.node && this.editor?.tab) {
			if (this.create) {
				this.editor.tab.nodeAdd(this.node, this.operations);
			}
			else {
				this.editor.tab.nodeUpdate(this.node, this.operations);
			}
		}
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.selected) {
			this.node = this.selected ? _.cloneDeep(this.selected.node) : null;
			this.operations = this.selected?.operations;
			this.create = this.selected?.create || false;
			this.tab = this.selected?.tab;

			this.$timeout(() => {
				if (this.editor?.tab) {
					if (this.node?.allocators) {
						if (this.node.allocators.length === 0) {
							this.node.allocators.push(this.#roleTous.kid);
							if (!this.editor.tab.schema.kidlist.some(
								(/** @type {any} */ item) => item.kid === this.#roleTous.kid,
							)) {
								this.editor.tab.schema.kidlist.push(this.#roleTous);
							}
						}

						// Échange atomique : on résout dans un tableau local, et la liste affichée
						// n'est remplacée qu'à la toute fin. Vider d'abord laissait l'interface
						// avec une liste vide dès que la repopulation était interrompue — une
						// exception levée par un script tiers pendant ce callback, par exemple —
						// et submit() persistait ensuite ce vide. Voir le $watchCollection de
						// diagnostic dans $onInit.
						/** @type {any[]} */
						const nextAllocators = [];
						for (const allocator of this.node.allocators) {
							for (const item of this.editor.tab.schema.kidlist) {
								if (allocator === item.kid) {
									nextAllocators.push(item);
								}
							}
						}
						this.mf.allocators.length = 0;
						this.mf.allocators.push(...nextAllocators);
					}

					if (this.node instanceof State) {
						// Même échange atomique que pour les affectataires, pour la même raison.
						/** @type {any[]} */
						const nextDelegationGranted = [];
						if (this.node.delegationGranted) {
							for (const delegationGranted of this.node.delegationGranted) {
								for (const item of this.editor.tab.schema.kidlist) {
									if (delegationGranted === item.kid) {
										nextDelegationGranted.push(item);
									}
								}
							}
						}
						this.mf.delegationGranted.length = 0;
						this.mf.delegationGranted.push(...nextDelegationGranted);

						if (this.node.droit?.length > 0) {
							for (const droit of this.node.droit) {
								if (!droit.role.isa) {
									const d = this.editor.tab.schema.kidlist
										.find((/** @type {any} */ item) => item.kid === droit.role.kid);
									if (d) {
										droit.role.isa = d.isa;
									}
								}
							}
						}
					}
					else if (this.node?.allocators) {
						// Comportement d'origine conservé : la liste des délégations était vidée
						// dès que le nœud portait des affectataires, y compris pour une action.
						this.mf.delegationGranted.length = 0;
					}

					this.$scope.$apply();
				}
			});

			if (this.create) {
				if (this.node instanceof State && this.node.isa.objid === 'K_NODE;WF_STATE' && this.editor?.tab?.schema) {
					this.node.initial = this.editor.tab.schema.states.length === 0 ? 1 : 0;
				}

				this.node?.addDroit({
					kright:    8,
					role:  {
						kid:  'K_NODE;KR_WF_TOUS',
						isa:  'K_NODE;KROLE',
						name: this.$translate.instant('common.all'),
					},
				});
			}

			if (this.node instanceof Action && this.editor?.tab?.schema) {
				for (const state of this.editor.tab.schema.states) {
					// @ts-ignore
					this.states.push({
						kid:  state.kid,
						name: state.getName(),
					});
				}
				for (const flow of [
					'from',
					'to',
				]) {
					this.tmp[flow] = [];
					// @ts-ignore
					for (const kid of Object.keys(this.node[`state${flow}`])) {
						// @ts-ignore
						const state = this.editor.tab.schema.states[this.editor.tab.schema.getKeyStateFromKid(kid)];
						if (state) {
							this.tmp[flow].push({
								kid,
								name: state.getName(),
							});
						}
					}
				}
				// if (this.node.kjson) {
				// 	let kjson;
				// 	try {
				// 		kjson = JSON.parse(this.node.kjson);
				// 	}
				// 	catch {
				// 		// eslint-disable-next-line prefer-destructuring
				// 		kjson = this.node.kjson;
				// 	}
				// 	if (kjson) {
				// 		if ('commentRequired' in kjson) {
				// 			this.node.commentRequired = kjson.commentRequired;
				// 		}
				// 		if ('enableTargetStateAllocators' in kjson) {
				// 			this.node.enableTargetStateAllocators = kjson.enableTargetStateAllocators;
				// 		}
				// 	}
				// }
			}
		}
	}

	$onInit() {
		// Diagnostic d'un bug signalé en production, non reproductible à ce jour : la liste des
		// affectataires se vide parfois d'elle-même, sans action de l'utilisateur. Le garde-fou
		// de submit() empêche la perte, mais il faut encore identifier la cause. Ce watcher ne
		// journalise que la transition « non vide -> vide » sur une étape qui a des affectataires,
		// pour que la prochaine occurrence laisse une trace exploitable dans la console du client
		// sans qu'il ait à outiller son navigateur.
		this.$scope.$watchCollection('$ctrl.mf.allocators', (
			/** @type {any[]} */ allocators,
			/** @type {any[]} */ previous,
		) => {
			if (
				allocators?.length === 0
				&& previous?.length > 0
				&& this.node?.allocators?.length > 0
			) {
				console.warn(
					'[wf-node-editor] la liste des affectataires s\'est vidée sans action utilisateur. '
					+ 'Merci de transmettre ce message complet au support Knitiv.',
					{
						nodeKid:        this.node.kid,
						nodeAllocators: [...this.node.allocators],
						perdus:         previous.map(item => item?.kid),
						kidlist:        (this.editor?.tab?.schema?.kidlist ?? []).map(item => item?.kid),
						tab:            this.tab,
						create:         this.create,
						stack:          new Error('trace').stack,
					},
				);
			}
		});

		// if we select "K_NODE;KR_TOUS" in the MF, then we have to delete all other selected items
		// "K_NODE;KR_TOUS" should be the only one selected
		this.$scope.$watch('$ctrl.mf.allocators', (/** @type {any} */ allocators) => {
			if (allocators?.some((/** @type {any} */ allocator) => allocator.kid === this.#roleTous.kid)) {
				_.remove(allocators, allocator => allocator.kid !== this.#roleTous.kid);
			}
		}, true);

		this.$scope.$watch('$ctrl.formNode.$dirty', () => {
			if (this.selected && this.formNode) {
				this.selected.modified = this.formNode.$dirty;
			}
		});

		this.$scope.$on('admin-wf-submit-node', () => this.submit());
	}
}
